import {CUSTOM_ELEMENTS_SCHEMA, LOCALE_ID, NgModule} from '@angular/core';
import {NgOptimizedImage, provideImageKitLoader, registerLocaleData} from "@angular/common";
import {provideClientHydration, BrowserModule} from '@angular/platform-browser';

import localeNl from '@angular/common/locales/nl';

import {AppRoutingModule} from './app-routing.module';
import {environment} from '../environments/environment';

import {provideAuth, getAuth} from "@angular/fire/auth";
import {provideFirestore, getFirestore} from "@angular/fire/firestore";
import {initializeApp, provideFirebaseApp} from "@angular/fire/app";

import {provideHttpClient, withInterceptorsFromDi} from "@angular/common/http";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {GoogleMapsModule} from "@angular/google-maps";

import {MatDatepickerModule} from "@angular/material/datepicker";
import {MatNativeDateModule, MatOptionModule} from "@angular/material/core";
import {MatSidenavModule} from "@angular/material/sidenav";
import {MatSelectModule} from "@angular/material/select";
import {MatAutocompleteModule} from "@angular/material/autocomplete";
import {MatCheckboxModule} from "@angular/material/checkbox";
import {MatDialogModule} from "@angular/material/dialog";
import {MatSliderModule} from "@angular/material/slider";

import {GalleryModule} from '@ks89/angular-modal-gallery';

import {AppComponent} from './app.component';
import {HeaderComponent} from './header/header.component';
import {FooterComponent} from './footer/footer.component';
import {HomeComponent} from './home/home.component';
import {RegisterComponent} from './register/register.component';
import {RegisterOrganizationComponent} from './register-organization/register-organization.component';
import {LoginComponent} from './login/login.component';
import {EventsOverviewComponent} from './events-overview/events-overview.component';
import {EventCardComponent} from './event-card/event-card.component';
import {FaqComponent} from './faq/faq.component';
import {EventPageComponent} from './event-page/event-page.component';
import {ContactComponent} from './contact/contact.component';
import {EventCreateComponent} from './event-create/event-create.component';
import {OverviewFilterMenuComponent} from './overview-filter-menu/overview-filter-menu.component';
import {OrganizationsComponent} from './organizations/organizations.component';
import {ErrorComponent} from './error/error.component';
import {PrivacyComponent} from './privacy/privacy.component';
import {ProfilePageComponent} from './profile-page/profile-page.component';
import {ProfileDetailsComponent} from './profile-details/profile-details.component';
import {ProfileEventsOverviewComponent} from './profile-events-overview/profile-events-overview.component';
import {MyOrganizationComponent} from './my-organization/my-organization.component';
import {SupportComponent} from './support/support.component';
import {TruncatePipe} from './pipes/transform.pipe';
import {PasswordForgottenComponent} from './password-forgotten/password-forgotten.component';
import {PasswordChangeComponent} from './password-change/password-change.component';
import {EmailChangeComponent} from './email-change/email-change.component';
import {TimeDifferencePipe} from './pipes/time-difference.pipe';
import {MyOrganizationEventsOverviewComponent} from './my-organization-events-overview/my-organization-events-overview.component';
import {MyOrganizationDetailsComponent} from './my-organization-details/my-organization-details.component';
import {MyOrganizationAddressChangeComponent} from './my-organization-address-change/my-organization-address-change.component';
import {MyOrganizationContactChangeComponent} from './my-organization-contact-change/my-organization-contact-change.component';
import {MyOrganizationEmailChangeComponent} from './my-organization-email-change/my-organization-email-change.component';
import {EventDetailPageComponent} from './event-detail-page/event-detail-page.component';
import {SignUpModalComponent} from "./signup-modal/signup-modal.component";
import {ThankYouComponent} from './thank-you/thank-you.component';
import {AdminUserManageComponent} from './admin-user-manage/admin-user-manage.component';
import {AdminActivityManageComponent} from './admin-activity-manage/admin-activity-manage.component';
import {AdminOrganizationManageComponent} from './admin-organization-manage/admin-organization-manage.component';
import {ConfirmationModalComponent} from './confirmation-modal/confirmation-modal.component';
import {AdminUserDetailComponent} from './admin-user-detail/admin-user-detail.component';
import {AdminSidebarComponent} from './admin-sidebar/admin-sidebar.component';
import {AdminOrganizationDetailComponent} from './admin-organization-detail/admin-organization-detail.component';
import {AdminActivityDetailComponent} from "./admin-activity-detail/admin-activity-detail.component";
import {EventCardPlaceholderComponent} from './event-card-placeholder/event-card-placeholder.component';
import {CountToDirective} from "./directives/count-to.directive";

registerLocaleData(localeNl);

@NgModule({
  declarations: [
        AppComponent,
        HeaderComponent,
        HomeComponent,
        FooterComponent,
        LoginComponent,
        RegisterComponent,
        EventCardComponent,
        EventsOverviewComponent,
        RegisterOrganizationComponent,
        ContactComponent,
        FaqComponent,
        EventPageComponent,
        EventCreateComponent,
        OverviewFilterMenuComponent,
        OrganizationsComponent,
        ErrorComponent,
        PrivacyComponent,
        ProfilePageComponent,
        ProfileDetailsComponent,
        ProfileEventsOverviewComponent,
        MyOrganizationComponent,
        SupportComponent,
        TruncatePipe,
        PasswordForgottenComponent,
        PasswordChangeComponent,
        EmailChangeComponent,
        TimeDifferencePipe,
        MyOrganizationEventsOverviewComponent,
        MyOrganizationDetailsComponent,
        MyOrganizationAddressChangeComponent,
        MyOrganizationContactChangeComponent,
        MyOrganizationEmailChangeComponent,
        EventDetailPageComponent,
        SignUpModalComponent,
        ThankYouComponent,
        AdminUserManageComponent,
        AdminActivityManageComponent,
        AdminOrganizationManageComponent,
        ConfirmationModalComponent,
        AdminUserDetailComponent,
        AdminSidebarComponent,
        AdminOrganizationDetailComponent,
        AdminActivityDetailComponent,
        EventCardPlaceholderComponent
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports:
    [
      GalleryModule,
      BrowserModule,
      GoogleMapsModule,
      AppRoutingModule,
      ReactiveFormsModule,
      BrowserAnimationsModule,
      MatDialogModule,
      MatNativeDateModule,
      MatDatepickerModule,
      MatSidenavModule,
      MatOptionModule,
      MatSelectModule,
      MatAutocompleteModule,
      MatCheckboxModule,
      MatSliderModule,
      NgOptimizedImage,
      FormsModule,
      CountToDirective
    ],
  providers: [
    MatDatepickerModule, { provide: LOCALE_ID, useValue: 'nl-NL' },
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAuth(() => getAuth()),
    provideFirestore(() => getFirestore()),
    provideClientHydration(),
    provideImageKitLoader('https://ik.imagekit.io/steddie/'),
    provideHttpClient(withInterceptorsFromDi()),
  ],
  bootstrap: [AppComponent]
})
export class AppModule {

}
